import type { GridFilterItem, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import type { Call } from 'services/types/call';
import type { CarInsuranceQuotesResponse } from 'services/types/carInsuranceQuote';
import type {
  Client,
  ClientWithLastMessage,
  EditableAdminClientAttributes,
  EditableClientAttributes,
} from 'services/types/client';
import type { ClientCar } from 'services/types/clientCar';
import type { Document } from 'services/types/document';
import type { Lead } from 'services/types/lead';
import type { SendTemplate } from 'services/types/message';
import type { Sale, SaleExternalDuplicate } from 'services/types/sale';

import baseApi from './baseApi';

/** One tab badge: a key plus the fully composed filter items (base + external + that tab's filter)
 *  whose matching-row count the backend returns alongside the list, in the same request. */
export type ClientCountBucket = {
  key: string;
  items: GridFilterItem[];
};

export type BankOffer = {
  bank: string;
  status: string;
  mappedStatus: string;
  maxAmount: number | null;
  maxTerm: number | null;
  expiredDate: string | null;
  rawBankResponse: Record<string, unknown> | null;
};

const clientsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getClients: build.query({
      query: (params: {
        paginationModel: { page: number; pageSize: number };
        filterModel: GridFilterModel;
        sortModel: GridSortModel;
        searchTerm?: string;
        businessUnitId?: number;
        businessUnitType?: 'car_dealership' | 'financial_institution' | 'insurance_broker' | null;
        countBuckets?: ClientCountBucket[];
      }) => ({
        url: `/clients?paginationModel=${JSON.stringify(params.paginationModel)}&filterModel=${JSON.stringify(
          params.filterModel
        )}&sortModel=${JSON.stringify(params.sortModel)}&searchTerm=${params.searchTerm || ''}&businessUnitId=${
          (params.businessUnitId || 0) > 0 ? params.businessUnitId : ''
        }&businessUnitType=${params.businessUnitType || ''}${
          params.countBuckets && params.countBuckets.length > 0
            ? `&countBuckets=${encodeURIComponent(JSON.stringify(params.countBuckets))}`
            : ''
        }`,
        method: 'GET',
      }),
      transformResponse: (response: { clients: Client[]; total: number; counts?: Record<string, number> }) => {
        return {
          clients: response.clients,
          total: response.total,
          counts: response.counts ?? {},
        };
      },
      providesTags: [{ type: 'Clients' as const, id: 'LIST' }],
    }),
    getClient: build.query({
      query: (id: string) => ({
        url: `/clients/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: Client): Client => response,
      providesTags: (_result, _error, id) => [{ type: 'Client' as const, id }],
    }),
    markAsRead: build.mutation({
      query: (id: number) => ({
        url: `/clients/${id}/mark-as-read`,
        method: 'PATCH',
      }),
      transformResponse: (response: { success: boolean }) => response,
      // Read/unread flips a client's pending status, which the queue badges and list both count.
      invalidatesTags: [{ type: 'Clients' as const, id: 'LIST' }],
    }),
    markAsUnread: build.mutation({
      query: (id: number) => ({
        url: `/clients/${id}/mark-as-unread`,
        method: 'PATCH',
      }),
      transformResponse: (response: { success: boolean }) => response,
      invalidatesTags: [{ type: 'Clients' as const, id: 'LIST' }],
    }),
    getCreditStatus: build.mutation({
      query: (clientId: number) => ({
        url: `/clients/${clientId}/credit-status`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => response,
    }),
    updateEditableAttributes: build.mutation({
      query: (props: { id: number; body: EditableClientAttributes }) => ({
        url: `/clients/${props.id}/editable-attributes`,
        method: 'PATCH',
        body: props.body,
      }),
      transformResponse: (response: Client): Client => response,
      invalidatesTags: (_result, _error, props) => [{ type: 'Client' as const, id: String(props.id) }],
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedClient } = await queryFulfilled;
          dispatch(
            clientsApi.util.updateQueryData('getClient', String(props.id), (draft) =>
              Object.assign(draft, updatedClient)
            )
          );
        } catch {
          // mutation failed — leave cache as-is
        }
      },
    }),
    updateAdminEditableAttributes: build.mutation({
      query: (props: { id: number; body: EditableAdminClientAttributes }) => ({
        url: `/clients/${props.id}`,
        method: 'PATCH',
        body: props.body,
      }),
      transformResponse: (response: Client): Client => response,
      invalidatesTags: (_result, _error, props) => [{ type: 'Client' as const, id: String(props.id) }],
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedClient } = await queryFulfilled;
          dispatch(
            clientsApi.util.updateQueryData('getClient', String(props.id), (draft) =>
              Object.assign(draft, updatedClient)
            )
          );
        } catch {
          // mutation failed — leave cache as-is
        }
      },
    }),
    createClient: build.mutation({
      query: (body: {
        businessUnitId: number;
        firstName?: string;
        phone: string;
        lastName?: string;
        email?: string;
        rut?: string;
        dni?: string;
        brandOfInterest?: string;
        modelsOfInterest?: string[];
        source?: string;
        disabledAutomaticResponses?: boolean;
      }) => ({
        url: '/clients',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Client): Client => response,
    }),
    createManualLead: build.mutation({
      query: (body: {
        businessUnitId: number;
        phone: string;
        name: string;
        lastName?: string;
        rut?: string;
        email?: string;
        reservationNumber?: string;
        isBusinessHours: boolean;
        template: {
          templateName: string;
          languageCode?: string;
          components?: SendTemplate['components'];
          aiStatus?: 'enabled' | 'disabled' | null;
          // When present, the welcome template is scheduled instead of sent immediately.
          postAt?: string;
        };
      }) => ({
        url: '/clients/manual-lead',
        method: 'POST',
        body,
      }),
      transformResponse: (response: {
        clientId: number;
        isNewClient: boolean;
        messageSent: boolean;
        scheduled?: boolean;
        message?: string;
        error?: string;
      }) => response,
      invalidatesTags: [{ type: 'Clients' as const, id: 'LIST' }],
    }),
    adminSearchClientByMessage: build.query({
      query: (params: { query: string; businessUnitId?: number }) => ({
        url: '/admin/clients/search',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ClientWithLastMessage[]) => response,
    }),
    uploadClientToCRM: build.mutation({
      query: ({
        clientId,
        businessUnitId,
        uploadToGenesys = false,
        genesysCentralizerId,
        branchOfficeName,
      }: {
        clientId: number;
        businessUnitId: number;
        uploadToGenesys?: boolean;
        genesysCentralizerId?: string;
        branchOfficeName?: string;
      }) => ({
        url: '/CRMUploads/upload-client-to-CRM',
        method: 'POST',
        body: { clientId, businessUnitId, uploadToGenesys, genesysCentralizerId, branchOfficeName },
      }),
      transformResponse: (response: unknown) => response,
    }),
    extractClientInfo: build.mutation({
      query: (clientId: number) => ({
        url: `/admin/clients/extract-client-info/${clientId}`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => response,
    }),
    getClientLeads: build.query({
      query: (clientId: string) => ({
        url: `/clients/${clientId}/leads`,
        method: 'GET',
      }),
      transformResponse: (response: { leads: Lead[] }) => response,
    }),
    getCrmStages: build.query<{ stages: { stage: string; count: number }[] }, { businessUnitId: number }>({
      query: ({ businessUnitId }) => ({
        url: `/clients/crm-stages?businessUnitId=${businessUnitId}`,
        method: 'GET',
      }),
    }),
    getCrmSubStages: build.query<
      { subStages: { subStage: string; count: number }[] },
      { businessUnitId: number; stage: string }
    >({
      query: ({ businessUnitId, stage }) => ({
        url: `/clients/crm-substages?businessUnitId=${businessUnitId}&stage=${encodeURIComponent(stage)}`,
        method: 'GET',
      }),
    }),
    getClientSales: build.query<{ sales: Sale[] }, string>({
      query: (clientId) => ({
        url: `/clients/${clientId}/sales`,
        method: 'GET',
      }),
      providesTags: (_result, _error, clientId) => [{ type: 'Sales' as const, id: clientId }],
    }),
    getClientCars: build.query<{ cars: ClientCar[] }, string>({
      query: (clientId) => ({
        url: `/clients/${clientId}/cars`,
        method: 'GET',
      }),
    }),
    updateClientSale: build.mutation({
      query: (props: {
        clientId: string;
        saleId: number;
        body: {
          loanData?: Sale['loanData'];
          insuranceData?: Sale['insuranceData'];
          advancePercentage?: number | null;
          documentIds?: number[];
          referralData?: Sale['referralData'];
        };
      }) => ({
        url: `/clients/${props.clientId}/sales/${props.saleId}`,
        method: 'PATCH',
        body: props.body,
      }),
      transformResponse: (response: { sale: Sale; crmSync: 'updated' | 'skipped' | 'failed'; crmError?: string }) =>
        response,
      invalidatesTags: (_result, _error, props) => [{ type: 'Sales' as const, id: props.clientId }],
    }),
    createClientSale: build.mutation({
      query: (props: {
        clientId: string;
        body: {
          insuranceType: Sale['insuranceType'];
          loanData?: Sale['loanData'];
          insuranceData?: Sale['insuranceData'];
          advancePercentage?: number | null;
          documentIds?: number[];
          referralData?: Sale['referralData'];
        };
      }) => ({
        url: `/clients/${props.clientId}/sales`,
        method: 'POST',
        body: props.body,
      }),
      transformResponse: (response: { sale: Sale }) => response,
      invalidatesTags: (_result, _error, props) => [{ type: 'Sales' as const, id: props.clientId }],
    }),
    syncClientSaleToCrm: build.mutation({
      query: (props: { clientId: string; saleId: number }) => ({
        url: `/clients/${props.clientId}/sales/${props.saleId}/sync-to-crm`,
        method: 'POST',
      }),
      transformResponse: (response: {
        sale: Sale;
        // `duplicate` means Insurex already had the portabilidad and nothing was
        // sent to Salesforce.
        action: 'create' | 'update' | 'duplicate' | 'skipped';
        externalId?: string;
        duplicate?: SaleExternalDuplicate;
        documents?: {
          uploaded: number;
          failed: { documentId: number; documentType: string | null; error: string }[];
        };
      }) => response,
      invalidatesTags: (_result, _error, props) => [{ type: 'Sales' as const, id: props.clientId }],
    }),
    getInsurexSaleOptions: build.query({
      query: () => ({
        url: '/clients/insurex/sale-options',
        method: 'GET',
      }),
      transformResponse: (response: { loanInstitutions: string[]; insuranceCompanies: string[] }) => response,
    }),
    simulateClientSale: build.mutation({
      query: (props: {
        clientId: string;
        body: {
          institutionName: string;
          totalAskedAmount: number;
          pendingBalance: number;
          remainingQuotas: number;
          currency: string;
        };
      }) => ({
        url: `/clients/${props.clientId}/sales/simulate`,
        method: 'POST',
        body: props.body,
      }),
      transformResponse: (response: {
        loanProtectionInsurance: number | null;
        unemploymentInsurance: number | null;
        simulatedInstitutionSlug?: string;
        usedFallbackInstitution?: boolean;
      }) => response,
    }),
    getClientCalls: build.query<{ calls: Call[] }, string>({
      query: (clientId) => ({
        url: `/clients/${clientId}/calls`,
        method: 'GET',
      }),
    }),
    getClientBankOffers: build.query<{ bankOffers: BankOffer[] }, string>({
      query: (clientId) => ({
        url: `/clients/${clientId}/bank-offers`,
        method: 'GET',
      }),
    }),
    uploadClientsFromCSV: build.mutation({
      query: (props: {
        businessUnitId: number;
        outboundCampaignId: string;
        source: string;
        mappedHeaders: { headerName: string; attrCliente: string }[];
        separator: string;
        file: File;
      }) => {
        const formData = new FormData();
        formData.append('file', props.file);
        formData.append('businessUnitId', props.businessUnitId.toString());
        formData.append('outboundCampaignId', props.outboundCampaignId);
        formData.append('source', props.source);
        formData.append('mappedHeaders', JSON.stringify(props.mappedHeaders));
        formData.append('separator', props.separator);
        return {
          url: '/surveys/upload-outbound-campaign-clients',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: {
        createdClients: number[];
        updatedClients: number[];
        failedClients: { phone: string; error: string }[];
        skippedClients: { row: number; phone: string; reason: string }[];
        aborted: boolean;
      }) => response,
    }),
    mapCSVHeaders: build.mutation({
      query: (props: { headers: string[]; exampleData: string[][] }) => ({
        url: '/surveys/map-csv-headers',
        method: 'POST',
        body: props,
      }),
      transformResponse: (response: { mappedHeaders: { headerName: string; attrCliente: string }[] }) => response,
    }),
    getClientDocuments: build.query<Document[], string>({
      query: (clientId) => ({
        url: `/clients/${clientId}/documents`,
        method: 'GET',
      }),
      providesTags: (_result, _error, clientId) => [{ type: 'Documents' as const, id: clientId }],
    }),
    uploadClientDocument: build.mutation<Document[], { clientId: number; file: File; password?: string }>({
      query: ({ clientId, file, password }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (password) {
          formData.append('password', password);
        }
        return {
          url: `/clients/${clientId}/documents`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'Documents' as const, id: String(clientId) }],
    }),
    downloadClientDocument: build.mutation({
      query: ({ clientId, documentId }: { clientId: number; documentId: number }) => ({
        url: `/clients/${clientId}/documents/${documentId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    closeLead: build.mutation({
      query: (props: { id: number; closedState: string; closeLeadMessage?: string }) => ({
        url: `/clients/${props.id}/close-lead`,
        method: 'PATCH',
        body: {
          closedState: props.closedState,
          ...(props.closeLeadMessage && { closeLeadMessage: props.closeLeadMessage }),
        },
      }),
      transformResponse: (response: { success: boolean }) => response,
      // Closing a lead moves the client out of the pending/assigned queue — refresh badges + list.
      invalidatesTags: [{ type: 'Clients' as const, id: 'LIST' }],
    }),
    getCarInsuranceQuotes: build.query<CarInsuranceQuotesResponse, number>({
      query: (clientId) => ({
        url: `/carInsuranceQuotes/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response: CarInsuranceQuotesResponse) => response,
    }),
    getClientScoring: build.query({
      query: (clientId: string) => ({
        url: `/admin/clients/${clientId}/scoring`,
        method: 'GET',
      }),
      transformResponse: (response: {
        phone: string;
        businessUnitCount: number;
        businessUnits: Array<{
          id: number;
          name: string;
          type: string;
          phoneNumber: string;
        }>;
        clients: Array<{
          id: number;
          firstName: string | null;
          lastName: string | null;
          email: string | null;
          rut: string | null;
          dni: string | null;
          businessUnitId: number;
          businessUnit: {
            id: number;
            name: string;
            type: string;
          } | null;
          createdAt: string;
        }>;
      }) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetClientQuery,
  useGetClientsQuery,
  useGetCreditStatusMutation,
  useUpdateEditableAttributesMutation,
  useUpdateAdminEditableAttributesMutation,
  useAdminSearchClientByMessageQuery,
  useUploadClientToCRMMutation,
  useExtractClientInfoMutation,
  useGetClientLeadsQuery,
  useGetCrmStagesQuery,
  useGetCrmSubStagesQuery,
  useGetClientSalesQuery,
  useGetClientCarsQuery,
  useUpdateClientSaleMutation,
  useCreateClientSaleMutation,
  useSyncClientSaleToCrmMutation,
  useGetInsurexSaleOptionsQuery,
  useSimulateClientSaleMutation,
  useGetClientCallsQuery,
  useGetClientBankOffersQuery,
  useUploadClientsFromCSVMutation,
  useMapCSVHeadersMutation,
  useGetClientDocumentsQuery,
  useUploadClientDocumentMutation,
  useDownloadClientDocumentMutation,
  useCloseLeadMutation,
  useMarkAsUnreadMutation,
  useMarkAsReadMutation,
  useGetCarInsuranceQuotesQuery,
  useCreateClientMutation,
  useCreateManualLeadMutation,
  useGetClientScoringQuery,
} = clientsApi;
